import { useEffect, useState } from "react";
import { CheckIcon, ChevronRightIcon, RotateCcwIcon } from "lucide-react";
import type { SlotItem } from "../utils";
import { useSlotMutations } from "../hooks/useSlotMutations";

export function DoneSection({
  items,
  defaultOpen = false,
}: {
  items: SlotItem[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  // Auto-expand when the day finishes while the page is open.
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  if (items.length === 0) return null;

  return (
    <section className="rounded-2xl border border-border overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
      >
        <ChevronRightIcon
          className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}
        />
        <span className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
          Done · {items.length}
        </span>
      </button>

      {open && (
        <div className="divide-y divide-border/60 border-t border-border">
          {items.map((item) => (
            <DoneRow key={item.slot.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

function formatCompletedAt(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

function DoneRow({ item }: { item: SlotItem }) {
  const { undo, unskip, isLoading } = useSlotMutations(item.goalId, item.slot.id);

  const isSkipped = item.slot.status === "SKIPPED";

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 ${isLoading ? "opacity-60" : ""}`}
    >
      <span
        className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
          isSkipped ? "border-2 border-dashed border-muted-foreground/25" : "bg-primary"
        }`}
      >
        {!isSkipped && <CheckIcon className="w-3 h-3 text-primary-foreground" strokeWidth={3} />}
      </span>

      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="text-sm leading-snug truncate text-muted-foreground line-through">
          {item.slot.name}
        </span>
        {item.slot.completionKind === "RECOVERY" && (
          <span className="shrink-0 rounded-full border border-primary/25 bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
            recovery
          </span>
        )}
      </div>

      <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
        {isSkipped
          ? "skipped"
          : item.slot.completedAt
            ? formatCompletedAt(item.slot.completedAt)
            : ""}
      </span>

      <button
        onClick={() => (isSkipped ? unskip.mutate() : undo.mutate())}
        disabled={isLoading}
        aria-label={isSkipped ? `Undo skip for ${item.slot.name}` : `Undo ${item.slot.name}`}
        className="shrink-0 p-1.5 rounded-lg text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted transition-colors disabled:opacity-40"
      >
        <RotateCcwIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
